import { useState, useMemo, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Shield,
  Search,
  Filter,
  Eye,
  AlertTriangle,
  ChevronDown,
  Heart,
  Home,
  Zap,
  Users,
  Car,
  Bus,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
  Clock,
  ArrowLeft,
  Building2,
  FileText,
  ShoppingBag,
  MoreVertical,
  MoreHorizontal,
} from "lucide-react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Loader from "../../../components/misc/Loader";
import ApprovalModal from "./components/ApprovalModal";
import RejectionModal from "./components/RejectionModal";
import insurancePolicyService from "../../../api/InsurancePolicyService";

export default function InsurancePolicyManagement() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const providerSlug = searchParams.get('provider');
  const [policies, setPolicies] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  // selectedPolicy is used by modals, setSelectedPolicy will be used when actions are enabled
  // eslint-disable-next-line no-unused-vars
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const [loading, setLoading] = useState(true);
  const [providerInfo, setProviderInfo] = useState(null);

  // Get the previous path from location state or default to insurance policies
  const getBackPath = () => {
    // Check if there's a 'from' state passed during navigation
    if (location.state?.from) {
      return location.state.from;
    }
    // If we have a provider slug, we likely came from providers page
    // Otherwise, go back in history or default to insurance policies
    return null;
  };
  
  const handleBack = () => {
    const backPath = getBackPath();
    if (backPath) {
      navigate(backPath);
    } else if (window.history.length > 1) {
      // Go back in browser history
      navigate(-1);
    } else {
      // Fallback: remove provider filter and stay on insurance policies page
      setSearchParams({});
    }
  };

  useEffect(()=>{
    const fetchPolicies = async()=>{
      setLoading(true);
      try{
        let response;
        
        // If provider slug is provided, fetch provider-specific policies
        if (providerSlug) {
          response = await insurancePolicyService.getProviderPolicies(providerSlug);
        } else {
          // Otherwise, fetch all policies
          response = await insurancePolicyService.getInsurancePolicies();
        }

        if(response?.data?.status_code === 200){
          // Transform object with numeric keys to array
          const data = response?.data?.data || {};
          let policiesArray = [];
          
          // Handle different response structures
          if (Array.isArray(data)) {
            policiesArray = data;
          } else if (typeof data === 'object') {
            policiesArray = Object.values(data);
          }
          
          setPolicies(policiesArray);
          
          // If filtering by provider, extract provider info from first policy or response
          if (providerSlug) {
            if (policiesArray.length > 0 && policiesArray[0]?.insurance_provider) {
              setProviderInfo(policiesArray[0].insurance_provider);
            } else if (response?.data?.data?.provider) {
              // Provider info might be at the root level
              setProviderInfo(response.data.data.provider);
            } else {
              // If no policies but provider slug exists, we might need to fetch provider info separately
              // For now, set to null and show empty state
              setProviderInfo(null);
            }
          } else {
            setProviderInfo(null);
          }
        }else{
          toast.error(response?.data?.data?.reason || response?.message || "An error occurred while fetching policies");
          setPolicies([]);
          setProviderInfo(null);
        }
      }catch(error){
        toast.error(error.response?.data?.data?.reason || "An error occurred while fetching policies");
        setPolicies([]);
        setProviderInfo(null);
      }finally{
        setLoading(false);
      }
    }
    fetchPolicies();
  },[providerSlug])

  // Calculate dynamic stats
  const stats = useMemo(() => {
    const uniqueProviders = new Set(policies.map(p => p.insurance_provider?.slug)).size;
    const travelPolicies = policies.filter(p => 
      ['intercity_travel', 'okada_rider', 'daily_commuter'].includes(p.policy_type)
    ).length;
    const lifePolicies = policies.filter(p => p.policy_type === 'life_insurance').length;
    const generalPolicies = policies.filter(p => 
      ['health_insurance', 'home_insurance', 'accident_insurance'].includes(p.policy_type)
    ).length;
    
    return {
      total: policies.length,
      providers: uniqueProviders,
      travel: travelPolicies,
      life: lifePolicies,
      general: generalPolicies,
    };
  }, [policies]);

  // Filter policies
  const filteredPolicies = useMemo(() => {
    return policies.filter((policy) => {
      const matchesSearch =
        policy.policy_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        policy.insurance_provider?.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        policy.description?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesType = typeFilter === "all" || policy.policy_type === typeFilter;
      
      return matchesSearch && matchesType;
    });
  }, [policies, searchQuery, typeFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredPolicies.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedPolicies = filteredPolicies.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, typeFilter]);

  const handleApprove = async (policyId) => {
    try {
      const response = await insurancePolicyService.approvePolicy(policyId);
      if (response.status) {
        // refetch the policies again
        const response = await insurancePolicyService.getInsurancePolicies();
        if(response?.data?.status_code === 200){
          setPolicies(response?.data?.data);
        }else{
          toast.error(response?.data?.data?.reason || "An error occurred while approving policy");
        }
        toast.success(response.message || "Policy approved successfully!");
      } else {
        toast.error(response.message || "Failed to approve policy");
      }
    } catch (error) {
      toast.error("An error occurred while approving the policy");
    }
  };

  const handleReject = async (policyId, reason) => {
    try {
      const response = await insurancePolicyService.rejectPolicy(policyId, reason);
      if (response.data?.status_code === 200) {
        // refetch the policies again
        const response = await insurancePolicyService.getInsurancePolicies();
        if(response?.data?.status_code === 200){
          setPolicies(response?.data?.data);
        }else{
          toast.error(response?.data?.data?.reason || "An error occurred while rejecting policy");
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.data?.reason || "An error occurred while rejecting policy");
    }
  };

  const openApprovalModal = (policy) => {
    setSelectedPolicy(policy);
    setShowApprovalModal(true);
  };

  const openRejectionModal = (policy) => {
    setSelectedPolicy(policy);
    setShowRejectionModal(true);
  };

  // Action Menu Component with position detection
  const ActionMenu = ({ policy, index }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [menuPosition, setMenuPosition] = useState('bottom'); // 'top' or 'bottom'
    const menuRef = useRef(null);
    const buttonRef = useRef(null);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (menuRef.current && !menuRef.current.contains(event.target) &&
            buttonRef.current && !buttonRef.current.contains(event.target)) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        // Detect position
        if (buttonRef.current) {
          const rect = buttonRef.current.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const spaceBelow = viewportHeight - rect.bottom;
          const spaceAbove = rect.top;
          
          // If more space below, show dropdown, otherwise show dropup
          setMenuPosition(spaceBelow > 200 ? 'bottom' : 'top');
        }
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isOpen]);

    const menuItems = [
      {
        label: "View Details",
        icon: Eye,
        color: "text-blue-600 hover:bg-blue-50",
        onClick: () => {
          navigate(`/admin/insurance-policies/${policy.slug}`, {
            state: { 
              from: location.pathname + location.search,
              providerSlug: providerSlug 
            }
          });
          setIsOpen(false);
        }
      },
      {
        label: "View Claims",
        icon: FileText,
        color: "text-purple-600 hover:bg-purple-50",
        onClick: () => {
          navigate(`/admin/insurance/claims?policy=${policy.slug}`, {
            state: { 
              from: location.pathname + location.search,
              policySlug: policy.slug,
              policyName: policy.policy_name,
              providerSlug: providerSlug 
            }
          });
          setIsOpen(false);
        }
      },
      {
        label: "View Purchases",
        icon: ShoppingBag,
        color: "text-emerald-600 hover:bg-emerald-50",
        onClick: () => {
          navigate(`/admin/purchases`, {
            state: { 
              from: location.pathname + location.search,
              policySlug: policy.slug,
              policyName: policy.policy_name,
              providerSlug: providerSlug,
              filterByPolicy: true
            }
          });
          setIsOpen(false);
        }
      },
    ];

    // Add conditional actions
    if (policy.status === "pending" || policy.status === "rejected") {
      menuItems.push({
        label: "Approve",
        icon: CheckCircle,
        color: "text-green-600 hover:bg-green-50",
        onClick: () => {
          openApprovalModal(policy);
          setIsOpen(false);
        }
      });
    }

    if (policy.status === "pending" || policy.status === "approved") {
      menuItems.push({
        label: "Reject",
        icon: XCircle,
        color: "text-red-600 hover:bg-red-50",
        onClick: () => {
          openRejectionModal(policy);
          setIsOpen(false);
        }
      });
    }

    return (
      <div className="relative">
        <motion.button
          ref={buttonRef}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center justify-center p-2 text-gray-600 bg-white border-2 border-gray-200 rounded-lg transition-all hover:border-blue-300 hover:text-blue-600 hover:bg-blue-50"
          title="More actions"
        >
          <MoreVertical className="w-5 h-5" />
        </motion.button>

        <AnimatePresence>
          {isOpen && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-40"
                onClick={() => setIsOpen(false)}
              />
              
              {/* Menu */}
              <motion.div
                ref={menuRef}
                initial={{ opacity: 0, scale: 0.95, y: menuPosition === 'bottom' ? -10 : 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: menuPosition === 'bottom' ? -10 : 10 }}
                transition={{ duration: 0.2 }}
                className={`absolute right-0 z-50 w-56 mt-2 bg-white rounded-xl shadow-2xl border-2 border-gray-100 overflow-hidden ${
                  menuPosition === 'top' ? 'bottom-full mb-2' : 'top-full'
                }`}
                style={{
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.05)'
                }}
              >
                <div className="py-2">
                  {menuItems.map((item, idx) => {
                    const Icon = item.icon;
                    return (
                      <motion.button
                        key={idx}
                        whileHover={{ x: 4 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={item.onClick}
                        className={`flex items-center w-full px-4 py-3 text-sm font-medium transition-colors ${item.color}`}
                      >
                        <Icon className="mr-3 w-4 h-4" />
                        {item.label}
                      </motion.button>
                    );
                  })}
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const getPolicyTypeIcon = (insuranceType, policyType) => {
    if (insuranceType === "life_insurance") return Heart;
    if (insuranceType === "general_insurance") {
      if (policyType === "health_insurance") return Users;
      if (policyType === "home_insurance") return Home;
      if (policyType === "accident_insurance") return AlertTriangle;
      return Shield;
    }
    if (insuranceType === "travel_insurance") {
      if (policyType === "daily_commuter") return Bus;
      if (policyType === "intercity_travel") return Car;
      if (policyType === "okada_rider") return Zap;
    }
    return Shield;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: {
        bg: "bg-yellow-100",
        text: "text-yellow-700",
        icon: Clock,
        label: "Pending",
      },
      approved: {
        bg: "bg-green-100",
        text: "text-green-700",
        icon: CheckCircle,
        label: "Approved",
      },
      rejected: {
        bg: "bg-red-100",
        text: "text-red-700",
        icon: XCircle,
        label: "Rejected",
      },
    };
    return statusConfig[status] || statusConfig.pending;
  };

  if(loading){
    return <Loader fullScreen={true} text="Loading policies..."/>
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/50">
      <div className="px-4 py-6 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          {/* Provider Filter Banner - Show when providerSlug exists, even if providerInfo is null */}
          {providerSlug && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 p-3 sm:p-4 bg-gradient-to-r from-emerald-50 to-blue-50 border-2 border-emerald-200 rounded-xl shadow-sm"
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <button
                    onClick={handleBack}
                    className="flex-shrink-0 p-2 text-gray-600 hover:text-gray-900 hover:bg-white rounded-lg transition-colors"
                    title="Go back"
                  >
                    <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                  {providerInfo && (
                    <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
                      {providerInfo.company_logo ? (
                        <img
                          src={providerInfo.company_logo}
                          alt={providerInfo.company_name}
                          className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-lg object-cover border-2 border-emerald-200"
                        />
                      ) : (
                        <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-lg flex items-center justify-center">
                          <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-xs sm:text-sm font-medium text-gray-600">Viewing policies for</p>
                        <p className="text-sm sm:text-lg font-bold text-gray-900 truncate">{providerInfo.company_name}</p>
                      </div>
                    </div>
                  )}
                  {!providerInfo && (
                    <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
                      <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-lg flex items-center justify-center">
                        <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs sm:text-sm font-medium text-gray-600">Viewing policies for provider</p>
                        <p className="text-sm sm:text-lg font-bold text-gray-900 truncate">Provider</p>
                      </div>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => {
                    // Remove provider filter but stay on the same page
                    setSearchParams({});
                  }}
                  className="w-full sm:w-auto px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-emerald-700 bg-white border border-emerald-200 rounded-lg hover:bg-emerald-50 transition-colors"
                >
                  View All Policies
                </button>
              </div>
            </motion.div>
          )}

          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0 flex-1">
              <h1 className="flex flex-col sm:flex-row gap-2 sm:gap-3 items-start sm:items-center text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="flex-shrink-0 p-1.5 sm:p-2 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg sm:rounded-xl">
                    <Shield className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-white" />
                  </div>
                  <span className="break-words">
                    {providerSlug && providerInfo 
                      ? "Provider Policies"
                      : "All Insurance Policies"
                    }
                  </span>
                </div>
              </h1>
              <p className="mt-2 text-sm sm:text-base text-gray-600">
                {providerSlug && providerInfo
                  ? `Managing insurance policies from ${providerInfo.company_name}`
                  : "Browse and manage all insurance policies from providers across different categories"
                }
              </p>
            </div>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 gap-4 mb-6 lg:grid-cols-5"
        >
          <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm transition-all hover:shadow-md hover:scale-105">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-gray-600">Total Policies</p>
                <p className="mt-1 text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <div className="p-2 bg-blue-100 rounded-lg">
                <Shield className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm transition-all hover:shadow-md hover:scale-105">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-gray-600">Providers</p>
                <p className="mt-1 text-2xl font-bold text-purple-600">{stats.providers}</p>
              </div>
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm transition-all hover:shadow-md hover:scale-105">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-gray-600">Travel</p>
                <p className="mt-1 text-2xl font-bold text-green-600">{stats.travel}</p>
              </div>
              <div className="p-2 bg-green-100 rounded-lg">
                <Car className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </div>

          <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm transition-all hover:shadow-md hover:scale-105">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-gray-600">Life</p>
                <p className="mt-1 text-2xl font-bold text-blue-600">{stats.life}</p>
              </div>
              <div className="p-2 bg-blue-100 rounded-lg">
                <Heart className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm transition-all hover:shadow-md hover:scale-105">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-gray-600">General</p>
                <p className="mt-1 text-2xl font-bold text-indigo-600">{stats.general}</p>
              </div>
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Home className="w-5 h-5 text-indigo-600" />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="p-4 mb-6 bg-white rounded-xl border border-gray-200 shadow-sm"
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search policies, providers, or descriptions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="py-2 pr-4 pl-10 w-full rounded-lg border border-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Type Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 -translate-y-1/2" />
              <select
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value);
                  setCurrentPage(1); // Reset to first page when filter changes
                }}
                className="py-2 pr-10 pl-10 w-full rounded-lg border border-gray-300 transition-colors appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Policy Types</option>
                <optgroup label="TripSecure Insurance">
                  <option value="intercity_travel">Intercity Travel</option>
                  <option value="okada_rider">Okada Rider</option>
                  <option value="daily_commuter">Daily Commuter</option>
                </optgroup>
                <optgroup label="Life Insurance">
                  <option value="life_insurance">Life Insurance</option>
                </optgroup>
                <optgroup label="General Insurance">
                  <option value="health_insurance">Health Insurance</option>
                  <option value="home_insurance">Home Insurance</option>
                  <option value="accident_insurance">Accident Insurance</option>
                </optgroup>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 w-5 h-5 text-gray-400 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>
        </motion.div>

        {/* Policies List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="space-y-4"
        >
          {filteredPolicies.length === 0 ? (
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="p-12 text-center bg-white rounded-xl border-2 border-gray-300 border-dashed"
            >
              <div className="flex justify-center mb-4">
                <div className="p-4 bg-gray-100 rounded-full">
                  <Shield className="w-16 h-16 text-gray-400" />
                </div>
              </div>
              <h3 className="mb-2 text-xl font-bold text-gray-900">No Policies Found</h3>
              <p className="mb-4 text-gray-600">
                {providerSlug && providerInfo
                  ? `${providerInfo.company_name} hasn't created any insurance policies yet.`
                  : searchQuery || typeFilter !== "all" 
                  ? "No policies match your current filters. Try adjusting your search or filter criteria." 
                  : "No insurance policies available at the moment. Policies will appear here once providers submit them."}
              </p>
              {(searchQuery || typeFilter !== "all") && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setSearchQuery("");
                    setTypeFilter("all");
                    setCurrentPage(1);
                  }}
                  className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
                >
                  Clear Filters
                </motion.button>
              )}
            </motion.div>
          ) : (
            <>
              {paginatedPolicies.map((policy, index) => {
              const TypeIcon = getPolicyTypeIcon(policy.policy_type, policy.policy_type);

              return (
                <motion.div
                  key={policy.slug}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm transition-all hover:shadow-md sm:p-6"
                >
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    {/* Left Section */}
                    <div className="flex flex-1 gap-4">
                      <div className="flex-shrink-0">
                        {policy.insurance_provider?.company_logo ? (
                          <div className="overflow-hidden w-16 h-16 bg-white rounded-xl border-2 border-gray-200 shadow-sm">
                            <img
                              src={policy.insurance_provider.company_logo}
                              alt={`${policy.insurance_provider.company_name} logo`}
                              className="object-cover w-full h-full"
                              onError={(e) => {
                                // Fallback to icon if image fails to load
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            <div className="hidden justify-center items-center w-full h-full bg-gradient-to-r from-blue-500 to-indigo-600">
                              <TypeIcon className="w-6 h-6 text-white" />
                            </div>
                          </div>
                        ) : (
                          <div className="flex justify-center items-center p-3 w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl">
                            <TypeIcon className="w-6 h-6 text-white" />
                          </div>
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <h3 className="mb-1 text-lg font-semibold text-gray-900">
                          {policy.policy_name}
                        </h3>
                        <p className="mb-2 text-sm text-gray-600 line-clamp-2">
                          {policy.description}
                        </p>
                        <div className="flex flex-wrap gap-2 items-center">
                          <span className="px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded-full">
                            {policy.policy_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                          <span className="text-xs text-gray-500">
                            by {policy.insurance_provider.company_name}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Right Section - Actions */}
                    <div className="flex items-center gap-3">
                      {/* Status Badge */}
                      {(() => {
                        const statusBadge = getStatusBadge(policy.status || "pending");
                        const StatusIcon = statusBadge.icon;
                        return (
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${statusBadge.bg} ${statusBadge.text}`}>
                            <StatusIcon className="mr-1.5 w-3 h-3" />
                            {statusBadge.label}
                          </span>
                        );
                      })()}

                      {/* Action Menu Dropdown */}
                      <ActionMenu policy={policy} index={index} />
                    </div>
                  </div>
                </motion.div>
              );
            })}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-col gap-4 justify-between items-center p-4 mt-6 bg-white rounded-xl border border-gray-200 shadow-sm sm:flex-row">
                <div className="text-sm text-gray-600">
                  Showing {startIndex + 1} to {Math.min(endIndex, filteredPolicies.length)} of{" "}
                  {filteredPolicies.length} policies
                </div>

                <div className="flex gap-2 items-center">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    <span className="hidden ml-1 sm:inline">Previous</span>
                  </motion.button>

                  <div className="flex gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                      // Show first page, last page, current page, and pages around current
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      ) {
                        return (
                          <motion.button
                            key={page}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setCurrentPage(page)}
                            className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                              currentPage === page
                                ? "bg-blue-600 text-white"
                                : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            {page}
                          </motion.button>
                        );
                      } else if (page === currentPage - 2 || page === currentPage + 2) {
                        return (
                          <span key={page} className="px-2 py-2 text-gray-500">
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="hidden mr-1 sm:inline">Next</span>
                    <ChevronRight className="w-4 h-4" />
                  </motion.button>
                </div>
              </div>
            )}
            </>
          )}
        </motion.div>
      </div>

      {/* Modals */}
      <ApprovalModal
        isOpen={showApprovalModal}
        onClose={() => setShowApprovalModal(false)}
        policy={selectedPolicy}
        onApprove={handleApprove}
      />
      <RejectionModal
        isOpen={showRejectionModal}
        onClose={() => setShowRejectionModal(false)}
        policy={selectedPolicy}
        onReject={handleReject}
      />
    </div>
  );
}
